/**
 * 영역 지정 오버레이 컴포넌트
 */
import React, { useEffect, useRef, useState } from 'react';
import { Annotation } from '../../../types/book';
import { AreaSelectionOverlay, HighlightMarker } from '../styles';

interface AreaSelectionOverlayComponentProps {
  annotation: Annotation;
  paragraphId: string;
  onAnnotationClick: (annotation: Annotation, e: React.MouseEvent) => void;
  highlightedAnnotationId?: string | null;
}

const AreaSelectionOverlayComponent: React.FC<AreaSelectionOverlayComponentProps> = ({
  annotation,
  paragraphId,
  onAnnotationClick,
  highlightedAnnotationId,
}) => {
  const isHighlighted = highlightedAnnotationId === annotation.id;
  const containerRef = useRef<HTMLDivElement>(null);
  const [overlayStyle, setOverlayStyle] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    // paragraphId로 직접 DOM 요소 찾기
    const paragraphTextRef = document.querySelector(`[data-paragraph-id="${paragraphId}"]`) as HTMLDivElement | null;
    if (!paragraphTextRef) {
      return;
    }

    const updateOverlay = () => {
      if (!paragraphTextRef) return;

      try {
        const paragraphRect = paragraphTextRef.getBoundingClientRect();
        const textContentWrapper = paragraphTextRef.querySelector('[data-text-content]') as HTMLElement;
        if (!textContentWrapper) return;
        
        const range = document.createRange();
        const walker = document.createTreeWalker(
          textContentWrapper,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              const parent = node.parentElement;
              if (parent?.closest('[data-inline-note]')) {
                return NodeFilter.FILTER_REJECT;
              }
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );
        
        let currentOffset = 0;
        let startNode: Text | null = null;
        let startOffsetInNode = 0;
        let endNode: Text | null = null;
        let endOffsetInNode = 0;
        let firstTextNode: Text | null = null;
        let lastTextNode: Text | null = null;
        
        let node: Node | null;
        while ((node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE) {
            const textNode = node as Text;
            const textContent = textNode.textContent || '';
            const textLength = textContent.length;
            
            if (!firstTextNode) {
              firstTextNode = textNode;
            }
            lastTextNode = textNode;
            
            if (!startNode && currentOffset + textLength > annotation.range.startOffset) {
              startNode = textNode;
              startOffsetInNode = Math.max(0, Math.min(textLength, annotation.range.startOffset - currentOffset));
            }
            
            if (currentOffset + textLength >= annotation.range.endOffset) {
              endNode = textNode;
              endOffsetInNode = Math.max(0, Math.min(textLength, annotation.range.endOffset - currentOffset));
              break;
            }
            
            currentOffset += textLength;
          }
        }
        
        // 영역 지정의 경우 (type='box' 또는 color='#FFF0F3', startOffset=0, endOffset=전체 길이) 전체 단락을 감싸도록 처리
        if ((annotation.type === 'box' || annotation.color === '#FFF0F3') && annotation.range.startOffset === 0 && firstTextNode && lastTextNode) {
          startNode = firstTextNode;
          startOffsetInNode = 0;
          endNode = lastTextNode;
          endOffsetInNode = lastTextNode.textContent?.length || 0;
        }
        
        if (!startNode || !endNode) {
          return;
        }
        
        try {
          range.setStart(startNode, startOffsetInNode);
          range.setEnd(endNode, endOffsetInNode);
        } catch (e) {
          return;
        }
        
        const rects = range.getClientRects();
        if (rects.length === 0) {
          return;
        }
        
        let minLeft = Infinity;
        let minTop = Infinity;
        let maxRight = -Infinity;
        let maxBottom = -Infinity;
        
        for (let i = 0; i < rects.length; i++) {
          const rect = rects[i];
          if (rect.width > 0 && rect.height > 0) {
            minLeft = Math.min(minLeft, rect.left);
            minTop = Math.min(minTop, rect.top);
            maxRight = Math.max(maxRight, rect.right);
            maxBottom = Math.max(maxBottom, rect.top + rect.height);
          }
        }

        if (minLeft === Infinity || minTop === Infinity || maxRight === -Infinity || maxBottom === -Infinity) {
          return;
        }

        const margin = 10;
        const wrapperRect = textContentWrapper.getBoundingClientRect();
        
        const clampedMinLeft = Math.max(minLeft, wrapperRect.left);
        const clampedMinTop = Math.max(minTop, wrapperRect.top);
        const clampedMaxRight = Math.min(maxRight, wrapperRect.right);
        const clampedMaxBottom = Math.min(maxBottom, wrapperRect.bottom);
        
        const textWidth = clampedMaxRight - clampedMinLeft;
        const textHeight = clampedMaxBottom - clampedMinTop;
        
        if (textWidth <= 0 || textHeight <= 0) return;
        
        const overlayTop = clampedMinTop - paragraphRect.top - margin;
        const overlayLeft = clampedMinLeft - paragraphRect.left - margin;
        const overlayWidth = textWidth + (margin * 2);
        const overlayHeight = textHeight + (margin * 2);
        
        setOverlayStyle({
          top: overlayTop,
          left: overlayLeft,
          width: overlayWidth,
          height: overlayHeight,
        });
      } catch (e) {
        // 오버레이 계산 중 오류 무시
      }
    };

    const initialTimeout = setTimeout(() => {
      requestAnimationFrame(updateOverlay);
    }, 50);
    
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateOverlay);
    });
    resizeObserver.observe(paragraphTextRef);
    
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(updateOverlay);
    });
    mutationObserver.observe(paragraphTextRef, { childList: true, subtree: true, characterData: true });

    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [paragraphId, annotation]);

  if (!overlayStyle) return null;

  return (
    <div ref={containerRef} style={{ display: 'contents' }}>
      <AreaSelectionOverlay
        $top={overlayStyle.top}
        $left={overlayStyle.left}
        $width={overlayStyle.width}
        $height={overlayStyle.height}
        data-area-selection="true"
        data-annotation-id={annotation.id}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAnnotationClick(annotation, e);
        }}
        style={{ pointerEvents: 'auto', cursor: 'pointer', position: 'relative' }}
        title="하이라이트 (클릭하여 제거)"
      >
        {isHighlighted && (
          <div style={{ position: 'absolute', top: '-8px', left: '-8px', zIndex: 1000 }}>
            <HighlightMarker data-highlight-marker />
          </div>
        )}
      </AreaSelectionOverlay>
    </div>
  );
};

export default AreaSelectionOverlayComponent;

